import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/auth.store';
import {
  loginRequest,
  logoutRequest,
  meRequest,
  registerRequest,
  type LoginInput,
  type RegisterInput,
} from './auth.api';

export function useCurrentUser() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const setUser = useAuthStore((s) => s.setUser);
  return useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      const user = await meRequest();
      setUser(user);
      return user;
    },
    enabled: !!accessToken,
    staleTime: 60_000,
    retry: false,
  });
}

export function useLogin() {
  const navigate = useNavigate();
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const setUser = useAuthStore((s) => s.setUser);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (input: LoginInput) => loginRequest(input),
    onSuccess: (data) => {
      setAccessToken(data.accessToken);
      setUser(data.user);
      qc.invalidateQueries({ queryKey: ['me'] });
      navigate('/', { replace: true });
    },
    onError: (error: unknown) => {
      toast.error(loginErrorMessage(error));
    },
  });
}

type ErrorResponse = { status?: number; data?: { message?: unknown; issues?: unknown } };

const responseOf = (error: unknown): ErrorResponse | undefined =>
  (error as { response?: ErrorResponse } | null)?.response;

/** Fallos que no dependen de lo que escribio el usuario: sin red, limite, 5xx. */
function transportErrorMessage(res: ErrorResponse | undefined): string | null {
  if (!res) return 'No se pudo conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.';
  if (res.status === 429) return 'Demasiados intentos. Espera un momento e inténtalo de nuevo.';
  if ((res.status ?? 0) >= 500) {
    return 'El servicio no está disponible por el momento. Inténtalo de nuevo en unos minutos.';
  }
  return null;
}

/**
 * Traduce el fallo de login a un mensaje honesto. Solo un 401 significa
 * "credenciales invalidas"; un 5xx o una red caida NO deben hacerle creer al
 * usuario que su contrasena esta mal.
 */
export function loginErrorMessage(error: unknown): string {
  const res = responseOf(error);
  if (res?.status === 401) return 'Credenciales inválidas. Verifica tu correo y contraseña.';
  return (
    transportErrorMessage(res) ??
    (typeof res?.data?.message === 'string' ? res.data.message : 'No se pudo iniciar sesión.')
  );
}

/**
 * Igual que login: el 409 ("RUC ya registrado") y el 400 (primer error de
 * validacion) se muestran tal cual; un 5xx o red caida no se disfrazan de
 * "revisa los datos".
 */
export function registerErrorMessage(error: unknown): string {
  const res = responseOf(error);
  const transport = transportErrorMessage(res);
  if (transport) return transport;
  const issues = res?.data?.issues;
  if (Array.isArray(issues)) {
    const first = (issues[0] as { message?: unknown } | undefined)?.message;
    if (typeof first === 'string') return first;
  }
  return typeof res?.data?.message === 'string'
    ? res.data.message
    : 'No se pudo crear la cuenta. Revisa los datos e intenta de nuevo.';
}

export function useRegister() {
  const navigate = useNavigate();
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const setUser = useAuthStore((s) => s.setUser);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (input: RegisterInput) => registerRequest(input),
    onSuccess: (data) => {
      setAccessToken(data.accessToken);
      setUser(data.user);
      qc.invalidateQueries({ queryKey: ['me'] });
      // Tras el alta: pantalla de bienvenida (zona horaria) y luego el recorrido.
      navigate('/bienvenida', { replace: true });
    },
    onError: (error: unknown) => {
      toast.error(registerErrorMessage(error));
    },
  });
}

export function useLogout() {
  const navigate = useNavigate();
  const clear = useAuthStore((s) => s.clear);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: () => logoutRequest(),
    onSettled: () => {
      clear();
      qc.clear();
      navigate('/login', { replace: true });
    },
  });
}
